// How a receipt's printed date/time becomes the trip's date and time. Shared by
// the basket scan (App.tsx applyReceiptMetadata, in the browser's own time zone)
// and the Bulk Import background worker (in the uploader's time zone, which the
// browser records with each upload).

export const parseReceiptDate = (date?: string | null) => {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const [year, month, day] = date.split('-').map(Number);
  const checked = new Date(Date.UTC(year, month - 1, day));
  if (year < 1900 || checked.getUTCFullYear() !== year || checked.getUTCMonth() !== month - 1 || checked.getUTCDate() !== day) return null;
  return { year, month, day };
};

export const parseReceiptTime = (time?: string | null) => {
  if (!time || !/^\d{2}:\d{2}(:\d{2})?$/.test(time)) return null;
  const [hours, minutes, seconds = 0] = time.split(':').map(Number);
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return { hours, minutes, seconds };
};

/**
 * Basket behavior: keep `base` (the trip's current date/time) and overwrite
 * the calendar day with the receipt's date, and the time of day with the
 * receipt's time when one was printed. Uses the runtime's local time zone.
 */
export function applyReceiptDateTime(base: Date, date?: string | null, time?: string | null): Date {
  const d = parseReceiptDate(date);
  const t = parseReceiptTime(time);
  const next = new Date(base);
  if (!d) return next;
  next.setFullYear(d.year, d.month - 1, d.day);
  if (t) next.setHours(t.hours, t.minutes, t.seconds, 0);
  return next;
}

// Wall-clock parts of an instant as seen in `timeZone`.
function zonedParts(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hours: get('hour'), minutes: get('minute'), seconds: get('second') };
}

// The instant whose wall clock in `timeZone` reads the given parts.
function instantFromZoned(p: { year: number; month: number; day: number; hours: number; minutes: number; seconds: number }, timeZone: string): Date {
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hours, p.minutes, p.seconds);
  // Offset of the zone at (roughly) that moment; a second pass settles DST edges.
  let guess = asUtc;
  for (let i = 0; i < 2; i++) {
    const seen = zonedParts(new Date(guess), timeZone);
    const seenUtc = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hours, seen.minutes, seen.seconds);
    guess += asUtc - seenUtc;
  }
  return new Date(guess);
}

/**
 * Same rule as applyReceiptDateTime, evaluated in an explicit IANA time zone
 * (e.g. "America/Los_Angeles"), for code that runs on a server in UTC.
 */
export function applyReceiptDateTimeInZone(base: Date, date: string | null | undefined, time: string | null | undefined, timeZone: string): Date {
  const d = parseReceiptDate(date);
  if (!d) return new Date(base);
  let zone = timeZone;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
  } catch {
    zone = 'UTC';
  }
  const wall = zonedParts(base, zone);
  const t = parseReceiptTime(time);
  return instantFromZoned({
    year: d.year, month: d.month, day: d.day,
    hours: t ? t.hours : wall.hours,
    minutes: t ? t.minutes : wall.minutes,
    seconds: t ? t.seconds : wall.seconds,
  }, zone);
}
